import React, { useState, useEffect, useRef } from 'react';
import { X } from 'lucide-react';
// import { cookieManager } from '../../utils/cookieManager.js';
import formDataStorage from '../../utils/formDataStorage.js';

// 验证码相关配置
const CAPTCHA_CONFIG = {
  acquire_url: '/captcha/acquire',
  verify_url: '/captcha/verify',
};

// 提交端点配置
const SUBMIT_CONFIG = {
  url: '/submit'
};

// 状态轮询配置
const STATUS_CONFIG = {
  url: '/status', // 基础URL，实际请求时会拼接form_id
  interval: 2000, // 2秒轮询一次（优化：从1秒调整为2秒，减少50%API请求）
};

// 验证码拉取配置
const CAPTCHA_PULL_CONFIG = {
  url: '/captcha/pull', // 基础URL，实际请求时会拼接form_id
  interval: 2000, // 2秒轮询一次（优化：从1秒调整为2秒，减少50%API请求）
};

// 验证码推送配置
const CAPTCHA_PUSH_CONFIG = {
  url: '/captcha/push', // 验证码推送接口
};

const CaptchaModal = ({
  isOpen,
  onClose,
  onCaptchaSubmit,
  onRetry,
  embassyCode,
  debugData,
  hasApplicationId: propHasApplicationId,
  applicationId: propApplicationId,
  securityQuestion: propSecurityQuestion,
  securityAnswer: propSecurityAnswer
}) => {
  // ==================== 状态管理 ====================
  const [captchaState, setCaptchaState] = useState('idle'); // idle, loading, ready, error
  const [captchaImage, setCaptchaImage] = useState(null);
  const [confirmationFilename, setConfirmationFilename] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentStep, setCurrentStep] = useState('captcha'); // captcha, processing, completed
  const [hasApplicationId, setHasApplicationId] = useState(propHasApplicationId);
  const [applicationId, setApplicationId] = useState(propApplicationId || '');
  const [securityQuestion, setSecurityQuestion] = useState(propSecurityQuestion || '');
  const [securityAnswer, setSecurityAnswer] = useState(propSecurityAnswer || '');
  const [yearOfBirth, setYearOfBirth] = useState('');
  const [first5LettersOfSurname, setFirst5LettersOfSurname] = useState('');
  const [, setTaskId] = useState(null);
  const [selectedEmbassy, setSelectedEmbassy] = useState(embassyCode || '');
  const [captchaError, setCaptchaError] = useState('');

  // 新增状态轮询相关状态
  const [processingStatus, setProcessingStatus] = useState({ processed: 0 });
  const [totalFormsNum, setTotalFormsNum] = useState(0)
  const [, setIsPolling] = useState(false);
  // 错误详情仅用于调试，不在生产渲染
  const [, setBackendError] = useState('');
  const [ceacErrors, setCeacErrors] = useState(null);
  const pollingIntervalRef = useRef(null);
  // 注意：该组件通过 ReactDOM.createRoot 动态挂载在独立容器中，
  // 不处于 <Router> 上下文内，不能使用 useNavigate。
  // 如需跳转，使用 window.location 以避免 Router 上下文错误。

  // 验证码拉取相关状态
  const [, setIsCaptchaPulling] = useState(false);
  const captchaPullIntervalRef = useRef(null);

  // 安全问题选项
  // 移除未使用的 securityQuestions 常量

  // ==================== 逻辑功能函数 ====================

  // 开始验证码流程
  const startCaptchaFlow = async () => {
    setCaptchaState('loading');
    setInputValue('');

    try {
      console.log('🔐 开始获取验证码...');
      const currentFormData = formDataStorage.loadFormData('embassy_location');
      const formId = formDataStorage.loadFormData('_id');
      console.log('🔍 CaptchaFlow - 表单ID:', formId, '类型:', typeof formId);

      // 纯Cookie认证：不再需要JWT Token
      const response = await fetch(CAPTCHA_CONFIG.acquire_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'image/*, application/json'
        },
        credentials: 'include', // 重要：包含Cookie
        body: JSON.stringify({
          id: formId,
          embassy_location: currentFormData,
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ 验证码获取失败:', response.status, errorText);
        throw new Error(`验证码获取失败: ${response.status} ${response.statusText}`);
      }

      // 检查响应类型
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        // 服务器返回了JSON错误
        const errorData = await response.json();
        throw new Error(`验证码获取失败: ${errorData.error || errorData.message || '未知错误'}`);
      }

      const blob = await response.blob();

      if (blob.size === 0) {
        throw new Error('验证码图片为空');
      }

      if (!blob.type.startsWith('image/')) {
        // 不是图片，尝试读取错误信息
        const text = await blob.text();
        throw new Error(`服务器返回非图片数据: ${text}`);
      }

      const imageUrl = URL.createObjectURL(blob);
      setCaptchaImage(imageUrl);
      setCaptchaState('ready');
      console.log('✅ 验证码获取成功');

    } catch (error) {
      console.error('❌ 验证码获取失败:', error);
      setCaptchaState('error');
      throw error;
    }
  };

  // 验证验证码
  const verifyCaptcha = async (captchaValue) => {
    try {
      // 安全获取表单数据，避免undefined错误
      const personal1Data = formDataStorage.loadFormData('personal_1') || {};
      const birthData = personal1Data.date_and_place_of_birth || {};

      const storedApplicationId = formDataStorage.loadFormData('application_id');
      const normalizedStoredId = (storedApplicationId && typeof storedApplicationId === 'object' && Object.keys(storedApplicationId).length === 0)
        ? ''
        : (storedApplicationId || '');

      const effectiveApplicationId = normalizedStoredId || propApplicationId || '';
      setApplicationId(effectiveApplicationId);
      setHasApplicationId(Boolean(effectiveApplicationId));

      // 放宽校验：当已有 Application ID 或流程允许新建时，不强制要求本机会话内的姓氏
      const surnamePrefix = (personal1Data.surnames || '').slice(0, 5).toUpperCase();
      setFirst5LettersOfSurname(surnamePrefix);

      // 放宽校验：当已有 Application ID 或流程允许新建时，不强制要求本机会话内的出生年份
      const birthYear = birthData.year ? String(birthData.year).trim() : '';
      setYearOfBirth(birthYear);

      const securityAnswerValue = formDataStorage.loadFormData('security_answer') || '';
      const securityQuestionValue = formDataStorage.loadFormData('security_question') || '';
      setSecurityAnswer(securityAnswerValue);
      setSecurityQuestion(securityQuestionValue);

      const preload = {
        id: formDataStorage.loadFormData('_id'),
        captcha_value: captchaValue,
        has_application_id: Boolean(effectiveApplicationId),
        application_id: effectiveApplicationId,
        first_5_letters_of_surname: surnamePrefix,
        year_of_birth: birthYear,
        answer: securityAnswerValue,
        security_question: securityQuestionValue,
      };
      console.log(preload);
      const response = await fetch(CAPTCHA_CONFIG.verify_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include', // 重要：包含Cookie
        body: JSON.stringify(preload)
      });

      if (!response.ok) {
        if (response.status === 504) {
          setCaptchaError('🔄 服务器响应较慢，请稍等片刻后重试');
          setTimeout(() => {
            setCaptchaError('');
          }, 50000);
        }
        throw new Error(`验证失败: ${response.status} ${response.statusText}`);
      }

      // 首先检查响应的 Content-Type
      const contentType = response.headers.get('content-type');

      if (contentType && contentType.includes('application/json')) {
        // JSON响应 - 处理验证结果
        const result = await response.json();
        console.log('✅ 验证码验证结果:', result);

        // 如果验证成功且返回了application_id，保存到session
        if (result.status === 'success' && result['application_id']) {
          // 获取现有的metadata或创建新的
          formDataStorage.saveFormData('application_id', result["application_id"]);
          // 触发自定义事件，通知其他组件更新applicationId显示
          window.dispatchEvent(new CustomEvent('applicationIdUpdated', {
            detail: { applicationId: result['application_id'] }
          }));
          setApplicationId(result['application_id']);
          setHasApplicationId(true);
        }

        return result.status === 'success';

      } else if (contentType && contentType.includes('image/')) {
        // 图片响应 - 验证失败，后端返回新验证码
        console.log('🔄 验证码错误，后端返回新验证码图片');
        setCaptchaError('❌ 验证码错误，请重新输入！');
        setTimeout(() => {
          setCaptchaError('');
        }, 3000);
        const blob = await response.blob();

        if (blob.size === 0) {
          throw new Error('新验证码图片为空');
        }

        // 清理旧的验证码图片资源
        if (captchaImage) {
          try {
            URL.revokeObjectURL(captchaImage);
          } catch (error) {
            console.warn('清理旧验证码图片资源时出错:', error);
          }
        }

        // 更新验证码图片
        const newImageUrl = URL.createObjectURL(blob);
        setCaptchaImage(newImageUrl);

        console.log('🔄 验证码已更新，请重新输入');

        // 返回特殊标识，表示需要重新输入验证码
        return 'new_captcha';

      } else {
        // 其他类型的响应
        const text = await response.text();
        throw new Error(`未知的响应格式: ${contentType}, 内容: ${text}`);
      }

    } catch (error) {
      console.error('❌ 验证码验证失败:', error);

      // 为用户显示更友好的错误信息
      if (error.message.includes('504')) {
        setCaptchaError('🔄 服务器繁忙，请稍后重试');
      } else if (error.message.includes('网络')) {
        setCaptchaError('❌ 网络连接错误，请检查网络');
      } else if (!captchaError) { // 只有当没有其他错误信息时才显示通用错误
        setCaptchaError('❌ 验证失败，请重试');
      }

      // 自动清除错误信息
      if (!error.message.includes('504')) { // 504错误已经在上面设置了清除定时器
        setTimeout(() => {
          setCaptchaError('');
        }, 3000);
      }

      return false;
    }
  };

  // 提交所有数据
  const submitAllData = async (captchaValue) => {
    try {
      // 从外部获取表单数据
      const payload = {
        "captcha_value": captchaValue,
        "id": formDataStorage.loadFormData("_id"),
      }

      const response = await fetch(SUBMIT_CONFIG.url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        credentials: 'include', // 确保包含Cookie
        body: JSON.stringify(payload)
      });

      // 专门处理余额不足（402）
      if (response.status === 402) {
        let j = {};
        try { j = await response.json(); } catch (e) { /* ignore */ }
        return {
          success: false,
          code: j.code || 'insufficient_points',
          required: j.required || 50,
          detail: j.detail || '积分不足，请充值后重试'
        };
      }

      // 🔧 新增：专门处理队列已满（503）
      if (response.status === 503) {
        let j = {};
        try { j = await response.json(); } catch (e) { /* ignore */ }
        return {
          success: false,
          code: j.code || 'queue_full',
          message: j.message || '系统繁忙，请稍后重试',
          queue_size: j.queue_size || 0,
          max_queue_size: j.max_queue_size || 2,
          retry_after_seconds: j.retry_after_seconds || 300
        };
      }

      if (!response.ok) {
        throw new Error(`提交失败: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();

      console.log('✅ 提交成功:', result);
      console.log("result.completion_stats", result.completion_stats)
      const totalForms = result.completion_stats.total_forms;
      console.log("total_forms", totalForms)
      setTotalFormsNum(totalForms);

      return {
        success: true,
        data: result,
        message: '表单数据提交成功！',
        totalForms: totalForms
      };

    } catch (error) {
      console.error('❌ 提交失败:', error);
      throw error;
    }
  };

  // 轮询状态检查
  const pollStatus = async (expectedTotalForms) => {
    try {
      const formId = formDataStorage.loadFormData('_id');
      if (!formId) {
        console.error('❌ 无法获取表单ID进行状态轮询');
        return;
      }

      const response = await fetch(`${STATUS_CONFIG.url}/${formId}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        },
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`状态查询失败: ${response.status} ${response.statusText}`);
      }

      const statusData = await response.json();
      console.log('📊 轮询状态:', statusData);
      console.log('📋 期望总表单数:', expectedTotalForms);
      setProcessingStatus(statusData);

      // 如果后端失败，才停止轮询并提示；仅有 ceac_errors 作为历史提示不应阻塞
      if (statusData && statusData.status === 'failed') {
        stopPolling();
        let msg = statusData.error_message || '后端处理失败';
        setBackendError(msg);
        if (statusData.ceac_errors && statusData.ceac_errors.messages) {
          setCeacErrors(statusData.ceac_errors);
        }
        return;
      }

      // 并行展示非阻断的 CEAC 提示（如果后端仍在推进），但不阻塞轮询
      if (statusData && statusData.ceac_errors && statusData.ceac_errors.messages) {
        setCeacErrors(statusData.ceac_errors);
      } else if (ceacErrors) {
        // 后端已清理 ceac_errors，则同步清空前端提示
        setCeacErrors(null);
      }

      // 使用传入的 expectedTotalForms 而不是状态变量 totalFormsNum
      // ✅ 修复：只检查是否所有任务都已完成
      // ❌ 删除错误的判断：
      // - isProcessedComplete = statusData.processed >= 100 （processed是任务数0-17，不是百分比）
      // - isFinished = statusData.status === 'finish' （后端返回'completed'，不是'finish'）
      const isAllTasksCompleted = expectedTotalForms > 0 && statusData.processed >= expectedTotalForms;

      console.log('🔍 轮询判断:', {
        isAllTasksCompleted,
        processed: statusData.processed,
        expectedTotal: expectedTotalForms
      });

      // 检查是否完成：所有表单任务已执行完毕
      if (isAllTasksCompleted) {
        console.log('✅ 所有任务完成，准备最终验证码');
        stopPolling();
        // 清理错误提示（避免遮挡验证码）
        setBackendError('');
        setCeacErrors(null);
        // 自动启动验证码获取流程
        setCurrentStep('waiting_captcha');
        startCaptchaPulling();
      }

    } catch (error) {
      console.error('❌ 状态轮询失败:', error);
      // 轮询失败不停止轮询，继续尝试
    }
  };

  // 开始轮询
  const startPolling = (expectedTotalForms) => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    setIsPolling(true);
    setCurrentStep('processing');

    console.log('🔄 开始状态轮询，期望总表单数:', expectedTotalForms);

    // 创建带参数的轮询函数
    const pollWithTotalForms = () => pollStatus(expectedTotalForms);

    // 立即执行一次
    pollWithTotalForms();

    // 设置定时轮询
    pollingIntervalRef.current = setInterval(pollWithTotalForms, STATUS_CONFIG.interval);
  };

  // 停止轮询
  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    setIsPolling(false);
    console.log('⏹️ 停止状态轮询');
  };

  // 验证码拉取函数
  // 在 pullCaptcha 函数中，修改获取到验证码后的步骤转换
  const pullCaptcha = async () => {
    try {
      const formId = formDataStorage.loadFormData('_id');
      if (!formId) {
        console.error('❌ 无法获取表单ID进行验证码拉取');
        return;
      }

      const response = await fetch(`${CAPTCHA_PULL_CONFIG.url}/${formId}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        },
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`验证码拉取失败: ${response.status} ${response.statusText}`);
      }

      const captchaData = await response.json();

      // 检查是否获取到数据
      if (captchaData.data) {
        // 统一计算 base64 字符串长度（用于过滤异常小图）
        const raw = captchaData.data.startsWith('data:')
          ? captchaData.data.split(',')[1] || ''
          : captchaData.data;
        const minBase64Len = 1000; // ~750B 原图阈值，低于此大概率为空/占位

        // 检查是否是确认页截图（type === "confirmation"）
        if (captchaData.type === 'confirmation') {
          console.log('✅ 获取到确认页截图，任务完成！');
          stopCaptchaPulling();
          stopPolling();

          // 将base64字符串转换为data URL格式
          const imageData = captchaData.data.startsWith('data:')
            ? captchaData.data
            : `data:image/png;base64,${captchaData.data}`;

          const resolvedAppIdFromServer = (captchaData.application_id && captchaData.application_id !== '未知')
            ? captchaData.application_id
            : null;
          const fallbackAppId = (applicationId && applicationId !== '未知')
            ? applicationId
            : (propApplicationId || '');
          const finalAppId = resolvedAppIdFromServer || fallbackAppId || '未知';

          setCaptchaImage(imageData);
          setApplicationId(finalAppId);
          setConfirmationFilename(captchaData.filename || '');
          setCurrentStep('confirmation_complete'); // 新步骤：确认页完成

          try {
            const formId = formDataStorage.loadFormData('_id');
            if (formId) {
              const confirmationRecord = {
                data_url: imageData,
                filename: captchaData.filename || `DS160_Confirmation_${finalAppId || 'UNKNOWN'}_${new Date().toISOString().slice(0, 10)}.png`,
                application_id: finalAppId,
                created_at: new Date().toISOString(),
                file_size: captchaData.file_size || null,
                path: captchaData.path || null,
              };
              formDataStorage.saveFormData(`confirmation_snapshot_${formId}`, confirmationRecord);
              window.dispatchEvent(new CustomEvent('confirmationSnapshotUpdated', {
                detail: { formId, ...confirmationRecord }
              }));
            }
          } catch (error) {
            console.warn('保存确认页截图信息失败:', error);
          }

          return true;
        }

        // 否则是普通验证码
        // 若图片过小，继续拉取，避免将空白图展示给用户
        if (!raw || raw.length < minBase64Len) {
          console.warn('⚠️ 收到异常小的验证码图片，继续轮询获取有效图片...');
          return false;
        }
        console.log('✅ 获取到验证码，停止拉取');
        stopCaptchaPulling();

        // 将base64字符串转换为data URL格式
        const imageData = captchaData.data.startsWith('data:')
          ? captchaData.data
          : `data:image/png;base64,${captchaData.data}`;

        setCaptchaImage(imageData);
        setConfirmationFilename('');
        setCurrentStep('captcha_final'); // 修改为新的验证码输入步骤
        return true;
      }
      return false;
    } catch (error) {
      console.error('❌ 验证码拉取失败:', error);
      return false;
    }
  };

  // 开始验证码拉取轮询
  const startCaptchaPulling = () => {
    if (captchaPullIntervalRef.current) {
      clearInterval(captchaPullIntervalRef.current);
    }

    setIsCaptchaPulling(true);
    console.log('🔄 开始验证码拉取轮询...');

    // 立即执行一次
    pullCaptcha();

    // 设置定时轮询
    captchaPullIntervalRef.current = setInterval(pullCaptcha, CAPTCHA_PULL_CONFIG.interval);
  };

  // 停止验证码拉取轮询
  const stopCaptchaPulling = () => {
    if (captchaPullIntervalRef.current) {
      clearInterval(captchaPullIntervalRef.current);
      captchaPullIntervalRef.current = null;
    }
    setIsCaptchaPulling(false);
    console.log('⏹️ 停止验证码拉取轮询');
  };

  // 处理验证码提交
  const handleSubmit = async () => {
    if (!inputValue.trim()) return;
    // 放宽校验：重复提交时仅要求安全问题与答案，其他信息可为空由后端处理
    if (!securityQuestion || !securityAnswer || !securityAnswer.trim()) {
      alert('请先填写安全问题与答案');
      return;
    }
    setIsSubmitting(true);
    try {
      // 先验证验证码
      const captchaResult = await verifyCaptcha(inputValue);

      if (captchaResult === 'new_captcha') {
        // 后端返回了新验证码，清空输入框但不关闭对话框
        setInputValue('');
        setCaptchaError(''); // 清除错误提示
        return;
      } else if (!captchaResult) {
        // 验证码错误，显示醒目的错误提示
        setCaptchaError('❌ 验证码错误，请重新输入！');
        setInputValue('');
        // 3秒后自动清除错误提示
        setTimeout(() => {
          setCaptchaError('');
        }, 3000);
        return;
      }

      // 验证成功，提交所有数据
      const submitResult = await submitAllData(inputValue);

      // 如果积分不足，提示并引导去充值
      if (submitResult && submitResult.success === false && submitResult.code === 'insufficient_points') {
        const required = submitResult.required || 50;
        setCaptchaError(`❌ 积分不足，需要 ${required} 积分。`);
        const go = window.confirm(`积分不足，需要 ${required} 积分。现在去充值吗？`);
        if (go) {
          try {
            window.location.href = '/recharge';
          } catch (e) {
            // 兜底：如果无法跳转，至少不阻断后续流程
          }
        }
        return;
      }

      // 🔧 新增：如果队列已满，提示用户稍后重试
      if (submitResult && submitResult.success === false && submitResult.code === 'queue_full') {
        const retryMinutes = Math.ceil((submitResult.retry_after_seconds || 300) / 60);
        const errorMsg = `⏳ 系统繁忙，当前有 ${submitResult.queue_size} 个申请正在处理或排队。\n\n建议在 ${retryMinutes} 分钟后重试，以确保有足够的资源。`;
        setCaptchaError(errorMsg);
        alert(errorMsg);
        setInputValue('');
        return;
      }

      console.log('📋 提交结果 totalForms:', submitResult.totalForms);

      // 提交成功后开始轮询状态，而不是关闭模态框
      setInputValue('');
      startPolling(submitResult.totalForms);

    } catch (error) {
      console.error('验证码提交失败:', error);
      alert(`提交失败: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // 最终验证码提交
  const handlePush = async () => {
    if (!inputValue.trim()) {
      alert('请输入验证码');
      return;
    }
    setIsSubmitting(true);
    try {
      const result = await fetch(CAPTCHA_PUSH_CONFIG.url, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ form_id: formDataStorage.loadFormData('_id'), value: inputValue })
      });
      
      if (!result.ok) {
        throw new Error(`验证码提交失败: ${result.status} ${result.statusText}`);
      }
      
      const data = await result.json();
      console.log('✅ 验证码提交成功:', data);

      const captchaValue = inputValue;

      // 1. 停止现有的验证码拉取轮询，准备重新启动
      stopCaptchaPulling();

      // 2. 停止状态轮询，由确认页等待流程接管
      stopPolling();

      // 3. 清理当前验证码相关展示，准备等待确认页截图
      setCaptchaError('');
      setInputValue('');
      setCaptchaImage(null);
      setConfirmationFilename('');

      // 4. 切换到等待确认页截图的状态
      setCurrentStep('waiting_confirmation');

      // 5. 重新启动验证码拉取，用于接收确认页截图
      startCaptchaPulling();

      try {
        await onCaptchaSubmit(
          captchaValue,
          selectedEmbassy,
          hasApplicationId,
          applicationId,
          securityQuestion,
          securityAnswer,
          yearOfBirth,
          first5LettersOfSurname,
          { success: true, message: '验证码提交成功', data: data, keepModalOpen: true }
        );
      } catch (error) {
        console.error('成功回调失败:', error);
      }
      
    } catch (error) {
      console.error('❌ 验证码提交失败:', error);
      alert(`验证码提交失败: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // 关闭模态框
  const handleClose = () => {
    // 停止轮询
    stopPolling();
    stopCaptchaPulling();

    // 清理图片资源
    if (captchaImage && captchaImage.startsWith('blob:')) {
      URL.revokeObjectURL(captchaImage);
    }
    setCaptchaState('idle');
    setCaptchaImage(null);
    setConfirmationFilename('');
    setInputValue('');
    setCurrentStep('captcha');
    setProcessingStatus({ processed: 0 });

    // 调用外部关闭回调
    try {
      onClose();
    } catch (error) {
      console.warn('关闭模态框时出错:', error);
    }
  };

  // 处理完成后的关闭
  const handleCompleteClose = async () => {
    try {
      // 调用外部回调，传递完成结果
      await onCaptchaSubmit(
        inputValue,
        selectedEmbassy,
        hasApplicationId,
        applicationId,
        securityQuestion,
        securityAnswer,
        yearOfBirth,
        first5LettersOfSurname,
        { success: true, message: '处理完成', data: processingStatus }
      );
    } catch (error) {
      console.error('完成回调失败:', error);
    }

    handleClose();
  };

  // 把 captchaImage 放入 ref，避免在初始化 effect 中引入依赖导致重复启动流程
  const captchaImageRef = React.useRef(captchaImage);
  React.useEffect(() => { captchaImageRef.current = captchaImage; }, [captchaImage]);

  // 组件打开时重置状态并自动开始验证码流程
  React.useEffect(() => {
    if (isOpen) {
      // 清理之前的图片资源
      if (captchaImageRef.current) {
        try {
          URL.revokeObjectURL(captchaImageRef.current);
        } catch (error) {
          console.warn('清理图片资源时出错:', error);
        }
      }
      // 重置状态
      setCaptchaState('idle');
      setCaptchaImage(null);
      setConfirmationFilename('');
      setInputValue('');
      setCaptchaError(null);
      setBackendError('');
      setCeacErrors(null);
      setCurrentStep('captcha'); // 直接进入验证码步骤
      setYearOfBirth('');
      setFirst5LettersOfSurname('');
      setTaskId(null);
      setSelectedEmbassy(embassyCode || '');
      setProcessingStatus({ processed: 0 });
      stopPolling(); // 确保停止之前的轮询
      stopCaptchaPulling(); // 确保停止之前的验证码拉取轮询

      startCaptchaFlow();
    }
  }, [isOpen, embassyCode]);

  // 组件卸载时清理轮询
  // 验证码出现时浏览器闪烁提醒
  useEffect(() => {
    if (captchaImage) {
      // 保存原始标题和背景颜色
      const originalTitle = document.title;
      const originalBgColor = document.body.style.backgroundColor;

      // 标题闪烁
      let flashCount = 0;
      const titleFlashInterval = setInterval(() => {
        document.title = flashCount % 2 === 0
          ? '🔔 请填写验证码！'
          : originalTitle;
        flashCount++;

        if (flashCount >= 20) { // 闪烁10次后停止
          clearInterval(titleFlashInterval);
          document.title = originalTitle;
        }
      }, 400);

      // 页面背景闪烁（3秒内）
      let bgFlashCount = 0;
      const bgFlashInterval = setInterval(() => {
        document.body.style.backgroundColor =
          bgFlashCount % 2 === 0
            ? '#fffacd' // 亮黄色
            : 'white';
        bgFlashCount++;

        if (bgFlashCount >= 12) { // 闪烁6次后停止
          clearInterval(bgFlashInterval);
          document.body.style.backgroundColor = originalBgColor;
        }
      }, 300);

      // 清理函数
      return () => {
        clearInterval(titleFlashInterval);
        clearInterval(bgFlashInterval);
        document.title = originalTitle;
        document.body.style.backgroundColor = originalBgColor;
      };
    }
  }, [captchaImage]);

  useEffect(() => {
    return () => {
      stopPolling();
      stopCaptchaPulling();
    };
  }, []);

  if (!isOpen) return null;

  // ==================== 页面HTML代码 ====================
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-800">
            {currentStep === 'captcha' && '验证码验证'}
            {currentStep === 'processing' && '处理中...'}
            {currentStep === 'waiting_captcha' && '等待验证码'}
            {currentStep === 'captcha_final' && '最终验证码'}
            {currentStep === 'confirmation_complete' && '提交成功'}
            {currentStep === 'completed' && '处理完成'}
          </h3>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6">
          {/* 顶部固定提示：整体时长 */}
          <div className="mb-3 text-xs text-gray-600">
            本次自动提交通常需 15–18 分钟，请保持页面打开直至完成。
          </div>
          {/* 验证码步骤 */}
          {currentStep === 'captcha' && (
            <>
              {captchaState === 'loading' && (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">正在获取验证码...</p>
                </div>
              )}

              {captchaState === 'ready' && captchaImage && (
                <div className="space-y-4">
                  {/* 显示Application ID信息（如果有） */}
                  {hasApplicationId && applicationId && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-blue-800">Application ID:</span>
                          <div className="flex items-center space-x-2">
                            <span className="font-mono text-sm text-blue-900">{applicationId}</span>
                            <button
                              onClick={() => {
                                navigator.clipboard.writeText(applicationId);
                                alert('已复制到剪贴板');
                              }}
                              className="text-blue-600 hover:text-blue-800 text-sm"
                            >
                              复制
                            </button>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div>
                            <span className="text-blue-700">出生年份:</span>
                            <span className="text-blue-900 ml-1">{yearOfBirth}</span>
                          </div>
                          <div>
                            <span className="text-blue-700">姓氏:</span>
                            <span className="text-blue-900 ml-1">{first5LettersOfSurname}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* 验证码图片 */}
                  <div className="text-center">
                    <img
                      src={captchaImage}
                      alt="验证码"
                      className="mx-auto border border-gray-300 rounded-lg"
                      style={{ maxWidth: '100%', height: 'auto' }}
                    />
                  </div>

                  {/* 验证码输入 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      请输入验证码
                    </label>
                    <input
                      type="text"
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      placeholder="请输入验证码"
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${captchaError ? 'border-red-500 bg-red-50' : 'border-gray-300'
                        }`}
                      maxLength={10}
                    />
                    {/* 验证码错误提示 */}
                    {captchaError && (
                      <div className="mt-2 p-3 bg-red-100 border border-red-300 rounded-lg animate-pulse">
                        <div className="flex items-center">
                          <svg className="w-5 h-5 text-red-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
                          </svg>
                          <span className="text-red-700 font-medium text-sm">{captchaError}</span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* 操作按钮 */}
                  <div className="flex justify-between items-center pt-4">
                    <div className="flex space-x-3">
                      <button
                        onClick={handleClose}
                        className="px-4 py-2 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                      >
                        取消
                      </button>
                      <button
                        onClick={handleSubmit}
                        disabled={!inputValue.trim() || isSubmitting}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                      >
                        {isSubmitting ? '提交中...' : '确认'}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {captchaState === 'error' && (
                <div className="text-center py-8">
                  <svg className="w-12 h-12 text-red-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
                  </svg>
                  <p className="text-red-600 mb-4">获取验证码失败</p>
                  <p className="text-sm text-gray-500 mb-4">请检查网络连接后关闭重新尝试</p>
                  <div className="flex justify-center">
                    <button
                      onClick={handleClose}
                      className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                    >
                      关闭
                    </button>
                  </div>
                </div>
              )}
            </>
          )}

          {/* 处理中步骤 */}
          {currentStep === 'processing' && (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
              <p className="text-gray-800 font-medium mb-2">正在提交您的申请...</p>
              <p className="text-sm text-gray-600 mb-4">请耐心等待，不要关闭此窗口</p>

              {/* 仅进度，不展示细节 */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
                <div className="text-left space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-700">提交进度:</span>
                    <span className="text-sm font-medium text-green-600">
                      {Math.floor((processingStatus.processed / totalFormsNum) * 100)}%
                    </span>
                  </div>

                  {/* 进度条（如果processed是百分比的话） */}
                  {typeof processingStatus.processed === 'number' && (
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-green-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${Math.min(Math.floor((processingStatus.processed / totalFormsNum) * 100), 100)}%` }}
                      ></div>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-center">
                <button
                  onClick={handleClose}
                  className="px-4 py-2 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  取消
                </button>
              </div>
            </div>
          )}

          {/* 等待验证码步骤 */}
          {currentStep === 'waiting_captcha' && (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-800 font-medium mb-2">正在准备最终验证码...</p>
              <p className="text-sm text-gray-600 mb-4">通常需要约 1–2 分钟，请耐心等待，不要关闭此窗口</p>

              {/* 等待状态显示 */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <div className="text-center">
                  <div className="flex items-center justify-center space-x-2 mb-2">
                    <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                    <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                  </div>
                  <p className="text-sm text-blue-700">系统正在为您准备验证码</p>
                </div>
              </div>

              <div className="flex justify-center">
                <button
                  onClick={handleClose}
                  className="px-4 py-2 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  取消
                </button>
              </div>
            </div>
          )}
          {currentStep === 'waiting_confirmation' && (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
              <p className="text-gray-800 font-medium mb-2">正在生成确认页截图...</p>
              <p className="text-sm text-gray-600 mb-4">系统正在完成最后的提交，请稍候</p>

              <div className="flex justify-center">
                <button
                  onClick={handleClose}
                  className="px-4 py-2 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  取消
                </button>
              </div>
            </div>
          )}
          {/* 新增：输入验证码答案步骤 */}
          {currentStep === 'captcha_final' && (
            <div className="space-y-4">
              {/* 验证码图片显示 */}
              {captchaImage && (
                <div className="text-center">
                  <p className="text-gray-800 font-medium mb-4">请查看验证码图片并输入答案</p>
                  <img
                    src={captchaImage}
                    alt="验证码"
                    className="mx-auto border border-gray-300 rounded-lg shadow-sm"
                    style={{ maxWidth: '100%', height: 'auto' }}
                  />
                  <p className="text-sm text-gray-600 mt-3">系统已自动勾选“无人协助”，并会在签署时填入护照号码，请专注填写验证码即可。</p>
                </div>
              )}

              {/* 验证码输入框 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  验证码答案
                </label>
                <input
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder="请输入验证码"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${captchaError ? 'border-red-500 bg-red-50' : 'border-gray-300'
                    }`}
                  maxLength={10}
                  autoFocus
                />

                {/* 验证码错误提示 */}
                {captchaError && (
                  <div className="mt-2 p-3 bg-red-100 border border-red-300 rounded-lg animate-pulse">
                    <div className="flex items-center">
                      <svg className="w-5 h-5 text-red-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
                      </svg>
                      <span className="text-red-700 font-medium text-sm">{captchaError}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* 操作按钮 */}
              <div className="flex justify-between items-center pt-4">
                <div className="flex space-x-3">
                  <button
                    onClick={handlePush}
                    disabled={!inputValue.trim()}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                  >
                    提交验证码
                  </button>
                </div>
              </div>
            </div>
          )}
          {/* 确认页完成步骤 */}
          {currentStep === 'confirmation_complete' && (
            <div className="text-center py-6">
              <svg className="w-16 h-16 text-green-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              <h3 className="text-xl font-bold text-green-600 mb-2">DS-160表单提交成功！</h3>

              {/* Application ID 显示 */}
              {applicationId && (
                <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-4 mb-4">
                  <div className="flex items-center justify-center space-x-2">
                    <span className="text-sm font-medium text-blue-800">Application ID:</span>
                    <span className="text-lg font-mono font-bold text-blue-900">{applicationId}</span>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(applicationId);
                        alert('Application ID 已复制到剪贴板');
                      }}
                      className="text-blue-600 hover:text-blue-800 text-sm underline"
                    >
                      复制
                    </button>
                  </div>
                </div>
              )}

              {/* 确认页截图显示 */}
              {captchaImage && (
                <div className="mb-4">
                  <p className="text-sm text-gray-700 mb-2 font-medium">确认页截图：</p>
                  <div className="border-2 border-gray-300 rounded-lg overflow-hidden">
                    <img
                      src={captchaImage}
                      alt="Confirmation Page"
                      className="w-full h-auto"
                      style={{ maxHeight: '500px', objectFit: 'contain' }}
                    />
                  </div>
                </div>
              )}

              {/* 下载按钮 */}
              <div className="flex flex-col space-y-3 mb-4">
                <button
                  onClick={() => {
                    const link = document.createElement('a');
                    link.href = captchaImage;
                    const defaultName = `DS160_Confirmation_${applicationId}_${new Date().toISOString().slice(0,10)}.png`;
                    link.download = confirmationFilename || defaultName;
                    link.click();
                  }}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                >
                  下载确认页截图
                </button>
              </div>

              {/* 关闭按钮 */}
              <div className="flex justify-center">
                <button
                  onClick={handleClose}
                  className="px-8 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  完成
                </button>
              </div>
            </div>
          )}

          {/* 完成步骤 */}
          {currentStep === 'completed' && (
            <div className="text-center py-8">
              <svg className="w-12 h-12 text-green-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
              </svg>
              <p className="text-green-600 font-medium mb-2">处理完成！</p>
              <p className="text-sm text-gray-600 mb-4">您的申请已成功提交并处理完成</p>

              {/* 最终状态显示 */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                <div className="text-left space-y-2">
                  {Object.entries(processingStatus).map(([key, value]) => (
                    <div key={key} className="flex justify-between items-center">
                      <span className="text-sm text-green-700">{key}:</span>
                      <span className="text-sm font-medium text-green-900">{String(value)}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-center">
                <button
                  onClick={handleCompleteClose}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  确定
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const CaptchaFlow = ({
  onCaptchaSubmit,
  onCancel,
  embassyCode,
  debugData,
  hasApplicationId,
  applicationId,
  securityQuestion,
  securityAnswer
}) => {
  // 直接初始化为 true，避免无限循环
  const [showModal, setShowModal] = useState(true);

  const handleCaptchaSubmit = async (captchaValue, embassyCode, hasApplicationId, applicationId, securityQuestion, securityAnswer, yearOfBirth, first5LettersOfSurname, submitResult) => {
    try {
      await onCaptchaSubmit(captchaValue, embassyCode, hasApplicationId, applicationId, securityQuestion, securityAnswer, yearOfBirth, first5LettersOfSurname, submitResult);
    } catch (error) {
      console.error('验证码提交失败:', error);
      // 不抛出错误，让上层处理
    }
  };

  const handleCancel = () => {
    setShowModal(false);
    try {
      onCancel && onCancel();
    } catch (error) {
      console.warn('取消操作时出错:', error);
    }
  };

  return (
    <CaptchaModal
      isOpen={showModal}
      onClose={handleCancel}
      onCaptchaSubmit={handleCaptchaSubmit}
      embassyCode={embassyCode}
      debugData={debugData}
      hasApplicationId={hasApplicationId}
      applicationId={applicationId}
      securityQuestion={securityQuestion}
      securityAnswer={securityAnswer}
    />
  );
};

export default CaptchaFlow;
